import { useStableParams } from '../../../routes/useStableParams';
import { useGetAsoConfigurationQuery, useUpdateAsoConfigurationMutation } from '../apiAso';
import { useCallback, useEffect, useMemo } from 'react';
import debounce from 'lodash.debounce';
import { AsoScenarioConfiguration } from '@ydistri/api-sdk';
import { useAsoScenarioStatus } from '../hooks/useAsoScenarioStatus';

export const useAsoConfigurations = (): {
  asoScenarioId: number | undefined;
  config: AsoScenarioConfiguration | undefined;
  updateConfig: ReturnType<typeof useGetAsoConfigurationQuery>[0];
  handleChange: (patch: Partial<AsoScenarioConfiguration>) => void;
  debouncedHandleChange: (patch: Partial<AsoScenarioConfiguration>) => void;
} => {
  const { asoScenarioId } = useStableParams();
  const { isEditable } = useAsoScenarioStatus();

  const { data: config } = useGetAsoConfigurationQuery(asoScenarioId ?? 0, {
    skip: !asoScenarioId,
  });

  const [updateConfig] = useUpdateAsoConfigurationMutation();

  const handleChange = useCallback(
    (patch: Partial<AsoScenarioConfiguration>) => {
      if (asoScenarioId && isEditable) {
        void updateConfig({
          asoScenarioId,
          payload: patch,
        });
      }
    },
    [asoScenarioId, updateConfig, isEditable],
  );

  const debouncedHandleChange = useMemo(
    () =>
      debounce((patch: Partial<AsoScenarioConfiguration>) => {
        handleChange(patch);
      }, 450),
    [handleChange],
  );

  useEffect(() => {
    return () => {
      debouncedHandleChange.cancel();
    };
  }, [debouncedHandleChange]);

  return useMemo(
    () => ({
      asoScenarioId,
      config,
      updateConfig,
      handleChange,
      debouncedHandleChange,
    }),
    [asoScenarioId, config, updateConfig, handleChange, debouncedHandleChange],
  );
};
